import {
  Component,
  Inject,
  TemplateRef,
  ViewChild,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';
import { TuiHostedDropdownComponent, TuiAlertService } from '@taiga-ui/core';
import { tuiCreateTimePeriods } from '@taiga-ui/kit';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { TranslateService } from '@ngx-translate/core';
import { ServicesService } from 'src/app/services/service/services.service';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';
import { LanguageEnum } from 'src/app/enum/language.enum';
declare var moment: any;

@Component({
  selector: 'app-events-table',
  templateUrl: './events-table.component.html',
  styleUrls: ['./events-table.component.css'],
})
export class EventsTableComponent {
  readonly columns = ['date', 'hour', 'user', 'event'];
  readonly sizes = ['l', 'm', 's'] as const;
  size = this.sizes[0];
  @Input() events: any;
  @Input() timeZone: any;
  private readonly userNameCache = new Map<string, string>();

  constructor(
    private servicesSvc: ServicesService,
    public hs: HistoryServicesService,
    private ngZone: NgZone,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    await this.resolveEventUsers();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['events']) {
      await this.resolveEventUsers();
    }
  }

  private async resolveEventUsers(): Promise<void> {
    if (!Array.isArray(this.events) || this.events.length === 0) {
      return;
    }

    const resolvePromises = this.events.map(async (event: any) => {
      if (event?.executorName) {
        return;
      }

      const rawUser = this.getRawEventUser(event);
      if (!rawUser || !this.isUUID(rawUser)) {
        return;
      }

      if (event?.userDisplayName) {
        this.userNameCache.set(rawUser, event.userDisplayName);
        event.executorName = event.userDisplayName;
        return;
      }

      // Guid vacío: el evento no guardó ningún usuario, no hay nada que buscar en Graph.
      if (this.isEmptyUUID(rawUser)) {
        event.executorName = this.translate.instant(
          'detail_service.name_not_available'
        );
        return;
      }

      if (this.userNameCache.has(rawUser)) {
        event.executorName = this.userNameCache.get(rawUser);
        return;
      }

      try {
        const executor = await this.servicesSvc.acquireMSClient(rawUser);
        const resolvedName =
          executor?.displayName ||
          this.translate.instant('detail_service.name_not_available');
        this.userNameCache.set(rawUser, resolvedName);
        event.executorName = resolvedName;
      } catch {
        const fallbackName = this.translate.instant(
          'detail_service.name_not_available'
        );
        this.userNameCache.set(rawUser, fallbackName);
        event.executorName = fallbackName;
      }
    });

    await Promise.all(resolvePromises);
  }

  private getRawEventUser(event: any): string {
    return event?.actingUser || event?.user || event?._actingUser || '';
  }

  private isUUID(value: string): boolean {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
  }

  private isEmptyUUID(value: string): boolean {
    return value === '00000000-0000-0000-0000-000000000000';
  }

  formatTimeUnit(unit: any) {
    return unit < 10 ? `0${unit}` : unit;
  }

  getDate(date: any) {
    if (date?.eventDate?.completeDate) {
      const textCreationDate = String(date.eventDate.completeDate).split('T');
      return moment(textCreationDate[0]).format('DD/MM/YYYY');
    }

    if (date?.date?.seconds) {
      const tz = this.timeZone || moment.tz.guess();
      return moment.unix(Number(date.date.seconds)).tz(tz).format('DD/MM/YYYY');
    }

    if (typeof date === 'string') {
      const textCreationDate = date.split('T');
      return moment(textCreationDate[0]).format('DD/MM/YYYY');
    }

    return '-';
  }

  getDateHour(time: any) {
    if (time?.eventDate?.time) {
      const textTime = String(time.eventDate.time).split(':');
      if (textTime.length >= 2) {
        return textTime[0] + ':' + textTime[1];
      }
    }

    if (time?.date?.seconds) {
      const tz = this.timeZone || moment.tz.guess();
      return moment.unix(Number(time.date.seconds)).tz(tz).format('HH:mm');
    }

    if (typeof time === 'string') {
      const textTime = time.split(':');
      if (textTime.length >= 2) {
        return textTime[0] + ':' + textTime[1];
      }
    }

    return '-';
  }

  getEventUser(event: any): string {
    const rawUser = this.getRawEventUser(event);
    const resolvedUser =
      event?.executorName ||
      event?.userDisplayName ||
      this.userNameCache.get(rawUser) ||
      event?.user ||
      event?._actingUser ||
      event?.actingUser ||
      '-';

    if (this.isUUID(resolvedUser)) {
      return this.translate.instant('detail_service.name_not_available');
    }

    return resolvedUser;
  }

  private get reasonLangSuffix(): 'Es' | 'En' | 'Fr' {
    const lang = (this.translate.currentLang || LanguageEnum.DEFAULT)
      .toLowerCase()
      .split('-')[0];

    switch (lang) {
      case LanguageEnum.ENGLISH:
        return 'En';
      case LanguageEnum.FRENCH:
        return 'Fr';
      default:
        return 'Es';
    }
  }

  private pickTranslation(source: any): string {
    if (!source || typeof source !== 'object') {
      return '';
    }

    return (
      source[`reason${this.reasonLangSuffix}`]?.trim() ||
      source.reasonEs?.trim() ||
      source.reasonEn?.trim() ||
      source.reasonFr?.trim() ||
      ''
    );
  }

  getEventDescription(event: any): string {
    if (!event) {
      return '-';
    }

    const translated =
      this.pickTranslation(event.reasonTraductions) ||
      this.pickTranslation(event.eventData);
    if (translated) {
      return translated;
    }

    if (typeof event.eventData === 'string' && event.eventData.trim().length > 0) {
      return event.eventData;
    }

    return event.reason?.trim() || '-';
  }
  // events = [
  //     {
  //         'date':'09/08/24',
  //         'hour':'14:00',
  //         'user':'Plataforma',
  //         'event':'Se creó el servicio',
  //     },
  //     {
  //         'date':'09/08/24',
  //         'hour':'15:00',
  //         'user':'Plataforma',
  //         'event':'El Servicio se etiquetó como pendiente',
  //     },
  //     {
  //         'date':'09/08/24',
  //         'hour':'15:20',
  //         'user':'Plataforma',
  //         'event':'Se aprobó el servicio',
  //     }
  // ]
}